import { useEffect, useId, useRef, useState } from "react";

import {
  AccountRequestError,
  accountErrorMessage,
  getProjectPublicApiSettings,
  updateProjectPublicApiSettings,
  type ProjectPublicApiSettings,
} from "../../lib/account-client";

export interface ProjectAccessSettingsApi {
  getProjectPublicApiSettings: typeof getProjectPublicApiSettings;
  updateProjectPublicApiSettings: typeof updateProjectPublicApiSettings;
}

export const projectAccessSettingsApi: ProjectAccessSettingsApi = {
  getProjectPublicApiSettings,
  updateProjectPublicApiSettings,
};

export function ProjectAccessSettings({
  workspaceId,
  projectId,
  api = projectAccessSettingsApi,
}: {
  workspaceId: string;
  projectId: string;
  api?: ProjectAccessSettingsApi;
}) {
  const errorId = useId();
  const [settings, setSettings] = useState<ProjectPublicApiSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [pending, setPending] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const stopButtonRef = useRef<HTMLButtonElement>(null);
  const confirmStopRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);

  const load = async () => {
    const result = await api.getProjectPublicApiSettings(workspaceId, projectId);
    setSettings(result);
    return result;
  };

  useEffect(() => {
    let active = true;
    setSettings(null);
    setError(null);
    void api.getProjectPublicApiSettings(workspaceId, projectId)
      .then((result) => { if (active) setSettings(result); })
      .catch((cause: unknown) => {
        if (active) setError(accountErrorMessage(cause, "Doorway could not load public access settings."));
      });
    return () => { active = false; };
  }, [api, loadAttempt, projectId, workspaceId]);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (confirmStop) confirmStopRef.current?.focus(); }, [confirmStop]);

  const apply = async (operation: Parameters<typeof updateProjectPublicApiSettings>[2], success: string) => {
    setPending(true);
    setError(null);
    try {
      setSettings(await api.updateProjectPublicApiSettings(workspaceId, projectId, operation));
      setAnnouncement(success);
    } catch (cause) {
      if (cause instanceof AccountRequestError && cause.code === "PUBLIC_API_GENERATION_CONFLICT") {
        await load().catch(() => undefined);
        setError("These settings changed in another tab. Doorway refreshed the current policy. Review it before trying again.");
        setAnnouncement("The current public API policy was refreshed after a stale-tab conflict.");
      } else {
        setError(accountErrorMessage(cause, "Doorway could not update public access."));
        setAnnouncement("The public access change was not applied.");
      }
    } finally {
      setPending(false);
    }
  };

  if (!settings && !error) return <p role="status">Loading public access controls...</p>;
  if (!settings) return (
    <div>
      <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>
      <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>Retry loading public access</button>
    </div>
  );

  const stop = async () => {
    setConfirmStop(false);
    await apply({
      operation: "take_offline",
      expectedAuthorityGeneration: settings.status.authorityGeneration,
    }, "Public provider traffic is offline. Static content may remain available.");
    statusRef.current?.focus();
  };
  return (
    <section aria-labelledby={`public-access-${projectId}`} className="review-stack">
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
      <div>
        <p className="step-label">Public API access</p>
        <h3 id={`public-access-${projectId}`}>Current status and emergency stop</h3>
        <p ref={statusRef} tabIndex={-1}><strong>{statusLabel(settings.status.state)}</strong></p>
        <p>Authority generation {settings.status.authorityGeneration}</p>
        {settings.status.operatorStopped
          ? <p role="alert">Doorway has applied an operator emergency stop.</p>
          : null}
      </div>

      {error ? <p id={errorId} ref={errorRef} tabIndex={-1} role="alert">{error}</p> : null}

      {settings.status.publicEnabled && settings.permissions.canTakeOffline ? (
        <button
          ref={stopButtonRef}
          type="button"
          className="danger-button"
          disabled={pending}
          onClick={() => setConfirmStop(true)}
        >Take public API offline</button>
      ) : null}
      {(settings.status.state === "unconfigured"
        || settings.blockers.some((blocker) => blocker.nextAction === "REVIEW_PUBLIC_API_POLICY"))
        && settings.permissions.canEnable ? (
          <button type="button" disabled={pending} onClick={() => void apply({
            operation: "review_revision",
            expectedAuthorityGeneration: settings.status.authorityGeneration,
          }, "The exact active revision is reviewed and offline until the owner enables it.")}>Review current revision policy</button>
        ) : null}
      {settings.status.state === "owner_disabled" && settings.permissions.canEnable
        && !settings.blockers.some((blocker) => blocker.nextAction === "REVIEW_PUBLIC_API_POLICY") ? (
        <button type="button" disabled={pending} onClick={() => void apply({
          operation: "enable",
          expectedAuthorityGeneration: settings.status.authorityGeneration,
        }, "Public API access is active.")}>Enable public API access</button>
      ) : null}

      {confirmStop ? (
        <div role="alertdialog" aria-modal="true" aria-labelledby={`confirm-stop-${projectId}`}>
          <h4 id={`confirm-stop-${projectId}`}>Stop new provider calls now?</h4>
          <p>In-flight calls may finish. Doorway will block every new reservation before credential access.</p>
          <button ref={confirmStopRef} type="button" disabled={pending} onClick={() => void stop()}>Confirm emergency stop</button>
          <button type="button" onClick={() => {
            setConfirmStop(false);
            stopButtonRef.current?.focus();
          }}>Cancel</button>
        </div>
      ) : null}

      <section aria-labelledby={`public-blockers-${projectId}`}>
        <h4 id={`public-blockers-${projectId}`}>Launch blockers and required owner actions</h4>
        {settings.blockers.length ? (
          <ul>{settings.blockers.map((blocker) => (
            <li key={`${blocker.code}:${blocker.nextAction}`}>
              <strong>{blocker.message}</strong>
              <span>{blocker.nextAction}{blocker.ownerRequired ? " · Owner required" : ""}</span>
            </li>
          ))}</ul>
        ) : <p>No public API launch blockers.</p>}
      </section>

      {settings.status.trips.length > 0 ? (
        <section aria-labelledby={`incident-${projectId}`}>
          <h4 id={`incident-${projectId}`}>Incident recovery review</h4>
          {settings.status.trips.map((trip) => (
            <article key={`${trip.scopeKind}:${trip.scopeId}`} aria-labelledby={`incident-${projectId}-${trip.scopeKind}-${trip.scopeId}`}>
              <h5 id={`incident-${projectId}-${trip.scopeKind}-${trip.scopeId}`}>
                {scopeLabel(trip.scopeKind)} scope: {trip.scopeId}
              </h5>
              <dl>
                <dt>Cause</dt><dd>{trip.cause}</dd>
                <dt>Required remediation</dt><dd>{trip.remediationCode}</dd>
                <dt>Cooldown ends</dt><dd>{new Date(trip.cooldownUntil * 1000).toISOString()}</dd>
              </dl>
              {settings.permissions.canReactivateSevereTrip ? (
                <button type="button" disabled={pending} onClick={() => void apply({
                  operation: "reactivate",
                  expectedAuthorityGeneration: settings.status.authorityGeneration,
                  scopeKind: trip.scopeKind,
                  scopeId: trip.scopeId,
                  expectedControlGeneration: trip.controlGeneration,
                  acknowledgement: true,
                  remediationCode: trip.remediationCode,
                  completedRemediationGeneration: trip.requiredRemediationGeneration,
                }, `The reviewed ${trip.scopeKind} incident is resolved.`)}>
                  Acknowledge and reactivate {trip.scopeKind} scope {trip.scopeId}
                </button>
              ) : <p>Only the workspace owner can reactivate this severe trip.</p>}
            </article>
          ))}
        </section>
      ) : null}
    </section>
  );
}

function scopeLabel(scope: "project" | "action" | "connection"): string {
  return scope[0]!.toUpperCase() + scope.slice(1);
}

function statusLabel(state: ProjectPublicApiSettings["status"]["state"]): string {
  return ({
    unconfigured: "Policy setup required",
    active: "Public provider traffic active",
    challenged: "Human check required",
    throttled: "Public provider traffic throttled",
    automatically_suspended: "Public provider traffic automatically suspended",
    owner_disabled: "Public provider traffic offline",
    credential_disabled: "Credential disabled",
    operator_disabled: "Doorway emergency stop active",
  } as const)[state];
}
